
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import {
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    DatePicker,
    FormBreadcrumbs,
    GroupedField,
    GroupedFields,
    GroupedTable,
    GroupedTableRow,
    ICrfConditionParameters,
    IGroupedTableCellProps,
    IGroupedTableHeaderProps,
    Input,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { useContext } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { Box, Tooltip, Typography } from '@mui/material';
import { ConditionMode, ValidateOn } from '@ngt/forms-core';
import * as Dtos from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';
import { POSITIVE_PERCENTAGE_RANGE } from '../../../utils/formatting/NumericFormats';
import DateAndCheckboxField from '../fields/DateAndCheckboxField';
import SourceDocuments from '../../documents/SourceDocuments';
import SelectWithTooltip from '../inputs/SelectWithTooltip';

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const mammogramPerformed = ({ formState }: ICrfConditionParameters<Dtos.DiagnosticImaging>) => formState?.value?.mmg === Dtos.LookupYesNoType.Yes;
const mammogram2DDisclosed = ({ formState }: ICrfConditionParameters<Dtos.DiagnosticImaging>) => !formState?.value?.mmg2D;
const mammogramTomosynthesisDisclosed = ({ formState }: ICrfConditionParameters<Dtos.DiagnosticImaging>) => !formState?.value?.mmgTomo;
const mammogramContrastEnhancedDisclosed = ({ formState }: ICrfConditionParameters<Dtos.DiagnosticImaging>) => !formState?.value?.mmgCEM;

const ultrasoundPerformed = ({ formState }: ICrfConditionParameters<Dtos.DiagnosticImaging>) => formState?.value?.usPerformed === Dtos.LookupYesNoType.Yes;
const mriPerformed = ({ formState }: ICrfConditionParameters<Dtos.DiagnosticImaging>) => formState?.value?.mriPerformed === Dtos.LookupYesNoType.Yes;

/*
 * ----------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------
 */



const hormoneReceptorHeaders: IGroupedTableHeaderProps[] = [
    {
        columnName: 'receptor',
        content: '',
        TableCellProps: {
            sx: {
                width: '25%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'percentageNucleiStained',
        content: 'Symbol',
        TableCellProps: {
            sx: {
                width: '10%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'percentageNucleiStainedValue',
        content: 'Percentage Nuclei Stained',
        TableCellProps: {
            sx: {
                width: '20%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'stainedIntensity',
        content: 'Stained Intensity',
        TableCellProps: {
            sx: {
                width: '20%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'result',
        content: 'Result',
        TableCellProps: {
            sx: {
                width: '25%',
                minWidth: 0
            }
        }
    }
];

const oestrogenRow: IGroupedTableCellProps[] = [
    {
        columnName: 'receptor',
        content: (
            <Typography>Oestrogen receptor </Typography>
        ),
    },
    {
        columnName: 'percentageNucleiStained',
        content: (
            <Input
                name='erSymbol'
                component={Select}
            />
        ),
        propertyPath: 'erSymbol'
    },
    {
        columnName: 'percentageNucleiStainedValue',
        content: (
            <Tooltip
                arrow
                placement="left"
                title={
                    <Typography variant="body2">If ER staining is reported as a range, please provide top of range (e.g., 30-50 would be reported as 50)</Typography>
                }
            >
                <Box>
                    <Input
                        name='erStainedValue'
                        component={Numeric}
                        numberFormat={POSITIVE_PERCENTAGE_RANGE}
                    />
                </Box>
            </Tooltip>
        ),
        propertyPath: 'erStainedValue'
    },
    {
        columnName: 'stainedIntensity',
        content: (
            <Input
                name='erIntensity'
                component={SelectWithTooltip}
                tooltipProps={{
                    arrow: true,
                    placement: "left",
                    title: <Typography variant="body2">
                        If intensity is described in words: 
                        <br/>
                        None = 0,<br/>
                        Weak = 1,<br/>
                        Intermediate = 2,<br/>
                        Strong = 3<br/>
                    </Typography>
                }}
            />
        ),
        propertyPath: 'erIntensity'
    },
    {
        columnName: 'result',
        content: (
            <Input
                name='erCat'
                component={Select}
            />
        ),
        propertyPath: 'erCat'
    },
];

const progesteroneRow: IGroupedTableCellProps[] = [
    {
        columnName: 'receptor',
        content: (
            <Typography>Progesterone receptor </Typography>
        ),
    },
    {
        columnName: 'percentageNucleiStained',
        content: (
            <Input
                name='prSymbol'
                component={Select}
            />
        ),
        propertyPath: 'prSymbol'
    },
    {
        columnName: 'percentageNucleiStainedValue',
        content: (
            <Tooltip
                arrow
                placement="left"
                title={
                    <Typography variant="body2">If PR staining is reported as a range, please provide top of range (e.g., 30-50 would be reported as 50)</Typography>
                }
            >
                <Box>
                    <Input
                        name='prStainedValue'
                        component={Numeric}
                        numberFormat={POSITIVE_PERCENTAGE_RANGE}
                    />
                </Box>
            </Tooltip>
        ),
        propertyPath: 'prStainedValue'
    },
    {
        columnName: 'stainedIntensity',
        content: (
            <Input
                name='prIntensity'
                component={SelectWithTooltip}
                tooltipProps={{
                    arrow: true,
                    placement: "left",
                    title: <Typography variant="body2">
                        If intensity is described in words: 
                        <br/>
                        None = 0,<br/>
                        Weak = 1,<br/>
                        Intermediate = 2,<br/>
                        Strong = 3<br/>
                    </Typography>
                }}
            />
        ),
        propertyPath: 'prIntensity'
    },
    {
        columnName: 'result',
        content: (
            <Input
                name='prCat'
                component={Select}
            />
        ),
        propertyPath: 'prCat'
    },
];

const her2IhcRow: IGroupedTableCellProps[] = [
    {
        columnName: 'receptor',
        content: (
            <Typography>HER2 IHC </Typography>
        ),
    },
    {
        columnName: 'percentageNucleiStained',
        content: (
            <></>
        )
    },
    {
        columnName: 'percentageNucleiStainedValue',
        content: (
            <></>
        )
    },
    {
        columnName: 'stainedIntensity',
        content: (
            <></>
        )
    },
    {
        columnName: 'result',
        content: (
            <Input
                name='her2Ihc'
                component={Select}
            />
        ),
        propertyPath: 'her2Ihc'
    },
];

const her2IshRow: IGroupedTableCellProps[] = [
    {
        columnName: 'receptor',
        content: (
            <Typography>HER2 ISH </Typography>
        ),
    },
    {
        columnName: 'percentageNucleiStained',
        content: (
            <></>
        )
    },
    {
        columnName: 'percentageNucleiStainedValue',
        content: (
            <></>
        )
    },
    {
        columnName: 'stainedIntensity',
        content: (
            <></>
        )
    },
    {
        columnName: 'result',
        content: (
            <Input
                name='her2Ish'
                component={Select}
            />
        ),
        propertyPath: 'her2Ish'
    },
];

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

interface IDiagnosticImagingFormProps { }

const DiagnosticImagingForm: React.FunctionComponent<IDiagnosticImagingFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
            >
                <GroupedField
                    propertyPath="primaryTumourSide"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="primaryTumourDetection"
                    component={ToggleButtonGroup}
                />

                <GroupedFields>
                    <Typography
                        variant="h2"
                        sx={{px: 2, py: 3}}
                    >
                        Imaging Details
                    </Typography>

                    <GroupedField
                        propertyPath="mmg"
                        component={ToggleButtonGroup}
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={mammogramPerformed}
                        subscription={{ value: true }}
                    >
                        <DateAndCheckboxField 
                            label={
                                <Typography variant="body1">
                                    2D
                                </Typography>
                            }
                            condition={mammogram2DDisclosed}
                            datePropertyPath="mmg2DDate"
                            notDonePropertyPath="mmg2D"
                        />

                        <DateAndCheckboxField 
                            label={
                                <Typography variant="body1">
                                    Tomosynthesis
                                </Typography>
                            }
                            condition={mammogramTomosynthesisDisclosed}
                            datePropertyPath="mmgTomoDate"
                            notDonePropertyPath="mmgTomo"
                        />

                        <DateAndCheckboxField 
                            label={
                                <Typography variant="body1">
                                    Contrast Enhanced
                                </Typography>
                            }
                            condition={mammogramContrastEnhancedDisclosed}
                            datePropertyPath="mmgCEMDate"
                            notDonePropertyPath="mmgCEM"
                        />
                    </CrfCondition>

                    <GroupedField
                        propertyPath="usPerformed"
                        component={ToggleButtonGroup}
                    />
                    <CrfCondition
                        mode={ConditionMode.Enable}
                        condition={ultrasoundPerformed}
                        subscription={{ value: true }}
                    >
                        <GroupedField
                            propertyPath="usPerformedDate"
                            component={DatePicker}
                            label=""
                        />
                    </CrfCondition>

                    <GroupedField
                        propertyPath="mriPerformed"
                        component={ToggleButtonGroup}
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={mriPerformed}
                        subscription={{ value: true }}
                    >
                        <GroupedField
                            propertyPath="mriShowedParenchymalEnhancement"
                            component={ToggleButtonGroup}
                        />
                    </CrfCondition>
                </GroupedFields>

                <GroupedFields>
                    <Typography
                        variant="h2"
                        sx={{px: 2, py: 3}}
                    >
                        Diagnostic Core Biopsy
                    </Typography>

                    <GroupedField
                        propertyPath="coreBiopsyDate"
                        component={DatePicker}
                    />

                    <GroupedField
                        propertyPath="lymphovascularInvasionReported"
                        component={ToggleButtonGroup}
                    />
                </GroupedFields>

                <GroupedFields>
                    <Typography
                        variant="h2"
                        sx={{px: 2, py: 3}}
                    >
                        Hormone Receptor Status 
                    </Typography>

                    <GroupedTable 
                        headers={hormoneReceptorHeaders}
                        stickyHeader={true}
                    >
                        <GroupedTableRow cells={oestrogenRow} />
                        <GroupedTableRow cells={progesteroneRow} />
                        <GroupedTableRow cells={her2IhcRow} />
                        <GroupedTableRow cells={her2IshRow} />
                    </GroupedTable>
                </GroupedFields>

                <SourceDocuments />
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default DiagnosticImagingForm;
